/**
 * src/components/map/AgentTeleportLivePlayer.tsx
 *
 * Picture-in-picture style live-broadcast player that mounts in the
 * AgentTeleportHUD whenever the followed agent is on air. Tiny by
 * default (~160×96 thumbnail) so it doesn't crowd the cinematic
 * controls; the user can expand it to a larger inline preview or
 * push the underlying `<video>` to OS-level fullscreen with one
 * tap.
 *
 * Architecture
 * ------------
 *   1. As soon as `liveSession` lands on a status='live' broadcaster,
 *      we call `joinBroadcast({ broadcasterId, wallet, country, ... })`
 *      with the same plumbing ProfileOverlay's `handleWatch` uses —
 *      including `onMode` / `onAgentCaption` so the signaling-only
 *      (synthetic avatar canvas + TTS audio) path works exactly the
 *      same way it does inside the full overlay.
 *
 *   2. The `<video>` element renders the inbound MediaStream (RTP
 *      from a real broadcaster, or a canvas captureStream from the
 *      AgentBroadcastEngine when the broadcaster is signaling_only).
 *
 *   3. Audio starts MUTED (browser autoplay policy). Tapping the
 *      "Tap to unmute" pill (or anywhere on the video frame in
 *      compact mode) flips muted=false and — critically — calls
 *      engine.unlockAudio() in the SAME synchronous gesture so the
 *      AudioContext.resume() lands inside the user-gesture window.
 *      Without that, BufferSources scheduled by `pushCaption()`
 *      stay silent on iOS Safari (geoai team report 2026-05-06).
 *
 *   4. The expand button toggles a CSS scale class so the same
 *      mounted <video> element grows to ~320×180 without re-running
 *      the WebRTC setup. The fullscreen button defers to the
 *      browser's native `requestFullscreen()` so the user gets the
 *      OS overlay.
 *
 *   5. On unmount (followed agent changes, broadcast ends, teleport
 *      exits) we tear down the viewer + engine in the right order
 *      so neither leaks an open peer connection across teleport
 *      sessions.
 *
 * Why a separate component (not just minimising ProfileOverlay)?
 *   The minimised pill in ProfileOverlay still keeps the entire
 *   overlay's React subtree mounted (live tools, gifts, etc.) which
 *   bloats the teleport HUD. This component is a stripped-down
 *   one-purpose viewer: the teleport user sees a thumbnail, can
 *   hear/see the agent, and can expand if they want more — anything
 *   beyond that (chat, tools, settings) is one click away in the
 *   profile overlay (which they reach via the "Open profile" pill in
 *   the teleport selection panel).
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';

import {
  joinBroadcast,
  type ViewerController,
  type AgentCaptionEvent,
} from '../../services/webrtcBroadcastService';
import {
  createAgentBroadcastEngine,
  type AgentBroadcastEngine,
} from '../AgentBroadcastCanvas';

export interface AgentTeleportLivePlayerProps {
  /** broadcaster identity (for an agent broadcast this is the
   *  agent's G-address, not the operator wallet). */
  broadcasterId: string;
  /** Display name of the followed agent — shown next to the LIVE
   *  badge so the user knows whose stream they're watching. */
  displayName: string | null;
  /** Optional avatar — rendered as a 22px round thumbnail next to
   *  the display name. */
  avatarUrl: string | null;
  /** Viewer's wallet (for SFU room policy). Optional — the SFU
   *  doesn't require it for read-only viewing. */
  viewerWallet?: string | null;
  /** Numeric country code if known (analytics only). */
  country?: number | null;
  /** Fired when the broadcast ends (server-pushed `ended` reason or
   *  socket close). The HUD can use this to drop us. */
  onEnded?: (reason?: string) => void;
  /**
   * The host's main Mapbox GL map instance. When provided we render
   * a SECOND tiny passive Mapbox canvas next to the agent's video
   * (split-pane PIP) that mirrors this map's camera every frame —
   * lng/lat/zoom/bearing/pitch — so viewers in a watch party see
   * BOTH the agent's broadcast AND what the teleport host's
   * cinematic camera is currently looking at.
   *
   * Works without a second WebRTC broadcast: teleportHub already
   * syncs the host's camera into every viewer's local map, so the
   * mini canvas just reflects whatever the local map shows. When
   * `null`, the player falls back to its single-pane layout.
   */
  hostMap?: mapboxgl.Map | null;
  /** Mapbox style URL the host map is currently using — the mini
   *  map calls setStyle() when this prop changes so a host swap
   *  to dark / satellite is reflected in the PIP. */
  hostMapStyle?: string | null;
}

const PILL_BG    = 'rgba(15, 23, 42, 0.94)';
const PILL_BORDER = '1px solid rgba(99, 102, 241, 0.45)';

/** Inject the unmute-pulse CSS once. Tiny + idempotent. */
let _stylesInstalled = false;
function _installStyles() {
  if (_stylesInstalled || typeof document === 'undefined') return;
  _stylesInstalled = true;
  const s = document.createElement('style');
  s.setAttribute('data-gt-teleport-live-player', 'true');
  s.textContent = `
    @keyframes gt-tlp-live-pulse {
      0%   { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.55); }
      70%  { box-shadow: 0 0 0 9px rgba(239, 68, 68, 0);  }
      100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);    }
    }
    @keyframes gt-tlp-fade-in {
      0%   { opacity: 0; transform: translateY(-4px) scale(0.96); }
      100% { opacity: 1; transform: translateY(0)    scale(1);    }
    }
  `;
  document.head.appendChild(s);
}

export const AgentTeleportLivePlayer: React.FC<AgentTeleportLivePlayerProps> = ({
  broadcasterId,
  displayName,
  avatarUrl,
  viewerWallet,
  country,
  onEnded,
  hostMap = null,
  hostMapStyle = null,
}) => {
  _installStyles();

  // ── Viewer + engine refs ────────────────────────────────────────────
  // We hold these in refs (not state) so the join-on-mount effect
  // doesn't re-run when they're assigned. The MediaStream sits in
  // state because we want React to bind it to the <video> element's
  // srcObject reactively (the alternative — doing the assignment
  // imperatively in a useEffect — is also fine, but state-driven
  // keeps the render output declarative).
  const viewerRef          = useRef<ViewerController | null>(null);
  const engineRef          = useRef<AgentBroadcastEngine | null>(null);
  const videoRef           = useRef<HTMLVideoElement | null>(null);
  const captionsBufRef     = useRef<AgentCaptionEvent[]>([]);
  const MAX_PENDING_CAPTIONS = 5;

  const [stream, setStream]   = useState<MediaStream | null>(null);
  // Audio gated until the first user gesture. Default true keeps us
  // policy-safe; the unmute pill flips it to false.
  const [muted,  setMuted]    = useState<boolean>(true);
  // Three sizes: 'mini' (160×96), 'expanded' (320×180), 'fullscreen'
  // (browser-controlled). Mini is the default — the whole point of
  // this component is "doesn't take much space".
  const [size,   setSize]     = useState<'mini' | 'expanded'>('mini');
  // Live banner state — flipped from onEnded so the player gracefully
  // shows "Broadcast ended" for a beat instead of vanishing mid-frame.
  const [endedReason, setEndedReason] = useState<string | null>(null);

  // Drain buffered captions into the engine — extracted because the
  // welcome handler and the caption handler both need it. Mirrors
  // ProfileOverlay's pushCaptionToEngine.
  const pushCaptionToEngine = useCallback((eng: AgentBroadcastEngine, caption: AgentCaptionEvent) => {
    const text = (caption.text || '').trim();
    if (!text) return;
    let audioBlob: Blob | null = null;
    if (caption.audio_base64) {
      try {
        const bin = atob(caption.audio_base64);
        const buf = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
        audioBlob = new Blob([buf], { type: caption.mime || 'audio/mpeg' });
      } catch {
        audioBlob = null;
      }
    }
    eng.pushCaption({
      text,
      audio: audioBlob,
      durationMs: caption.duration_ms,
    }).catch(() => { /* push failures are non-fatal — caption text still renders */ });
  }, []);

  // ── Join lifecycle ──────────────────────────────────────────────────
  // Re-runs whenever the broadcaster identity changes (host swaps the
  // followed agent during teleport). We tear down the previous viewer
  // first so the previous PeerConnection is released cleanly.
  useEffect(() => {
    if (!broadcasterId) return;
    setEndedReason(null);
    captionsBufRef.current = [];

    const v = joinBroadcast({
      broadcasterId,
      wallet: viewerWallet || undefined,
      country: country == null ? undefined : Number(country),
      // We're a viewer that's pinned inside the cinematic teleport
      // HUD, not just somebody who tapped "Watch" on the agent
      // profile. Server uses this flag to auto-extend the broadcast
      // (free, in 60s chunks, capped) so the agent doesn't get
      // cut mid-cinematic — see paidLiveSessionService and the
      // teleportExtensionPolicyMirror gate.
      teleportAttached: true,
      onStream: (s) => setStream(s),
      onMode: (mode) => {
        // Mirrors ProfileOverlay.handleWatch: when the broadcaster is
        // signaling_only (the headless MCP / `start_agent_live` path)
        // we synthesise the video locally because no RTP will arrive.
        if (mode !== 'signaling_only') return;
        if (engineRef.current) return; // already rigged
        const eng = createAgentBroadcastEngine({
          agentPublicKey: broadcasterId,
          displayName,
          avatarUrl,
        });
        engineRef.current = eng;
        try { setStream(eng.getStream()); }
        catch { /* canvas.captureStream unsupported — captions still arrive */ }
        // Drain anything that landed in the gap between welcome and
        // engine instantiation (slow phones / first-paint races).
        const buffered = captionsBufRef.current.splice(0);
        for (const c of buffered) pushCaptionToEngine(eng, c);
      },
      onAgentCaption: (caption) => {
        const eng = engineRef.current;
        if (!eng) {
          const buf = captionsBufRef.current;
          buf.push(caption);
          while (buf.length > MAX_PENDING_CAPTIONS) buf.shift();
          return;
        }
        pushCaptionToEngine(eng, caption);
      },
      onEnded: (reason) => {
        setEndedReason(reason || 'ended');
        try { onEnded?.(reason); } catch { /* ignore */ }
      },
      onDenied: (reason) => {
        setEndedReason(reason || 'denied');
        try { onEnded?.(reason); } catch { /* ignore */ }
      },
      onError: (_err) => {
        // Errors are non-fatal — the viewer auto-reconnects when it
        // can. We deliberately don't surface a banner here because
        // the player is a tiny PIP; spamming errors would be worse
        // UX than a brief stutter.
      },
      shouldResume: () => true,
    });
    viewerRef.current = v;

    return () => {
      try { viewerRef.current?.stop(); } catch { /* swallow */ }
      viewerRef.current = null;
      try { engineRef.current?.dispose(); } catch { /* swallow */ }
      engineRef.current = null;
      setStream(null);
    };
  }, [broadcasterId, viewerWallet, country, displayName, avatarUrl, onEnded, pushCaptionToEngine]);

  // Bind / unbind the MediaStream to the <video> element. Using
  // srcObject (not src) gives us zero-copy live playback.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
  }, [stream]);

  // ── User-gesture audio unlock ───────────────────────────────────────
  // Click-anywhere-on-the-frame in mini mode unmutes (Twitter / TikTok
  // pattern). The expanded mode also surfaces a dedicated pill so it's
  // discoverable when the frame doesn't have an obvious affordance.
  const unmute = useCallback(() => {
    setMuted(false);
    const v = videoRef.current;
    if (v) {
      try { v.muted = false; } catch { /* ignore */ }
      try { void v.play(); } catch { /* gesture lost — user can re-tap */ }
    }
    const eng = engineRef.current;
    if (eng) {
      eng.unlockAudio().catch(() => { /* ignore — re-tap works */ });
    }
  }, []);

  // Native fullscreen via the browser's API. Falls back to expanding
  // inline when the API is missing (older mobile Safari) so the user
  // still gets a bigger preview.
  const goFullscreen = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const req = (v as any).requestFullscreen
            || (v as any).webkitRequestFullscreen
            || (v as any).msRequestFullscreen;
    if (typeof req === 'function') {
      try { req.call(v); return; }
      catch { /* fall through to inline expand */ }
    }
    setSize('expanded');
  }, []);

  // ── Geometry ────────────────────────────────────────────────────────
  // The video pane is 16:9. When a host map is provided we render a
  // SECOND square-ish pane to its right (the host-camera PIP) and
  // grow the total container width accordingly. Without a host map
  // we fall back to single-pane (original layout). Explicit pixel
  // dimensions (vs. fluid) keep the player from competing with the
  // style-chip cluster + place-search row for the same horizontal
  // band on narrow screens.
  const showHostPane = !!hostMap;
  const videoDim = size === 'mini'
    ? { width: 160, height: 90 }
    : { width: 280, height: 158 };
  const hostDim = size === 'mini'
    ? { width: 90,  height: 90 }
    : { width: 158, height: 158 };
  const totalWidth = showHostPane
    ? videoDim.width + hostDim.width
    : videoDim.width;
  // Kept for the ended-tombstone width below; everything else uses
  // videoDim / hostDim independently.
  const dim = { width: totalWidth, height: videoDim.height };

  // If the broadcast has ended, render a brief tombstone (auto-removed
  // when the parent unmounts us). This avoids a black-frame flash
  // between the end event and the followed-agent live session
  // disappearing from the live feed.
  if (endedReason) {
    return (
      <div
        role="status"
        style={{
          ...containerBaseStyle,
          width: dim.width,
          padding: '6px 10px',
          background: PILL_BG,
          border: PILL_BORDER,
          color: '#fca5a5',
          fontSize: 11,
          fontWeight: 600,
          textAlign: 'center',
        }}
      >
        Broadcast ended
      </div>
    );
  }

  return (
    <div
      style={{
        ...containerBaseStyle,
        width:        dim.width,
        background:   PILL_BG,
        border:       PILL_BORDER,
        animation:    'gt-tlp-fade-in 220ms ease-out',
      }}
    >
      {/* Header strip — display name + LIVE badge + size controls.
          Stays above the video frame so the user always knows whose
          broadcast they're watching even when the canvas is still
          rendering its first frame. */}
      <div
        style={{
          display:    'flex',
          alignItems: 'center',
          gap:        6,
          padding:    '4px 6px 4px 8px',
          fontSize:   11,
          color:      '#e2e8f0',
        }}
      >
        {avatarUrl && (
          <img
            src={avatarUrl}
            alt=""
            referrerPolicy="no-referrer"
            style={{
              width:        18,
              height:       18,
              borderRadius: '50%',
              objectFit:    'cover',
              flexShrink:   0,
            }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        )}
        <span
          style={{
            display:       'inline-flex',
            alignItems:    'center',
            gap:           4,
            padding:       '1px 6px',
            background:    '#dc2626',
            color:         '#fff',
            borderRadius:  999,
            fontWeight:    800,
            letterSpacing: 0.6,
            fontSize:      9,
            textTransform: 'uppercase',
            // Subtle pulse on the badge to reinforce the "live"
            // affordance — same rhythm as the marker ring so the
            // whole HUD feels coherent.
            animation:     'gt-tlp-live-pulse 1.6s ease-out infinite',
          }}
        >
          ● LIVE
        </span>
        <span
          style={{
            flex:        1,
            minWidth:    0,
            overflow:    'hidden',
            textOverflow: 'ellipsis',
            whiteSpace:  'nowrap',
            color:       '#cbd5e1',
            fontWeight:  600,
          }}
          title={displayName || broadcasterId}
        >
          {displayName || `${broadcasterId.slice(0, 4)}…${broadcasterId.slice(-4)}`}
        </span>
        <button
          type="button"
          onClick={() => setSize((s) => (s === 'mini' ? 'expanded' : 'mini'))}
          aria-label={size === 'mini' ? 'Expand player' : 'Collapse player'}
          title={size === 'mini' ? 'Expand to a larger preview' : 'Collapse to a thumbnail'}
          style={controlBtnStyle}
        >
          {size === 'mini' ? '⤢' : '⤡'}
        </button>
        <button
          type="button"
          onClick={goFullscreen}
          aria-label="Fullscreen"
          title="Fullscreen the broadcast"
          style={controlBtnStyle}
        >
          ⛶
        </button>
      </div>

      {/* Split-pane body: video (left) + optional host-camera PIP
          (right). When no host map is provided this collapses back
          to single-pane (original layout). The two children stay
          side-by-side at every size so viewers can read both
          streams at once without switching tabs. */}
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
        {/* Video frame. Click-to-unmute on mini mode; the unmute pill
            on top is the explicit affordance for desktop pointer users
            who might not realise the whole frame is clickable. */}
        <div
          style={{
            position:   'relative',
            width:      videoDim.width,
            height:     videoDim.height,
            background: '#000',
            cursor:     muted ? 'pointer' : 'default',
            overflow:   'hidden',
            // Only round the LEFT corners now — when the host pane is
            // visible, the right edge butts up against it. Conditional
            // rounding keeps the unified-pill look in single-pane mode
            // (original behaviour) without us having to ship two
            // different border-radius rules.
            borderBottomLeftRadius:  10,
            borderBottomRightRadius: showHostPane ? 0 : 10,
          }}
          onClick={muted ? unmute : undefined}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={muted}
            // Disable native PIP — we already provide our own; this
            // avoids two competing PIP indicators on Chromium.
            disablePictureInPicture
            style={{
              width:    '100%',
              height:   '100%',
              // `object-fit:cover` + a little zoom out tweak crops the
              // black bars that some agent-canvas captures emit at the
              // sides on weird aspect ratios. The synthetic Ken-Burns
              // canvas already renders 16:9 so this is a no-op there.
              objectFit:'cover',
              display:  'block',
            }}
          />

          {/* Tap-to-unmute pill — only while muted. */}
          {muted && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); unmute(); }}
              aria-label="Tap to unmute"
              style={{
                position:    'absolute',
                left:        '50%',
                bottom:      6,
                transform:   'translateX(-50%)',
                padding:     '4px 10px',
                borderRadius: 999,
                background:  'rgba(15, 23, 42, 0.85)',
                color:       '#fef3c7',
                fontSize:    10,
                fontWeight:  700,
                border:      '1px solid rgba(251, 191, 36, 0.45)',
                cursor:      'pointer',
                backdropFilter: 'blur(4px)',
                whiteSpace:  'nowrap',
              }}
            >
              🔈 Tap to unmute
            </button>
          )}
        </div>

        {/* Host-camera PIP — passive Mapbox canvas that mirrors the
            host's main map every move event. Renders nothing when no
            host map is available (e.g. early mount before App's map
            ref is set, or a future caller that doesn't pass it). */}
        {showHostPane && hostMap && (
          <HostMapPreview
            hostMap={hostMap}
            hostMapStyle={hostMapStyle}
            width={hostDim.width}
            height={hostDim.height}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Tiny passive Mapbox GL map that mirrors a host map's camera state.
 *
 * Why a separate component (not just an inline div):
 *   - Lets the lifecycle be scoped — when the player unmounts the
 *     mini map's WebGL context is released too. Inlining it would
 *     mean adding cleanup to a useEffect inside an already-large
 *     component which makes the lifecycle harder to reason about.
 *   - Re-mount on hostMap-prop swap: if the parent ever passes a
 *     different mapboxgl.Map, React tears down the old preview and
 *     spins up a new one bound to the new instance, no manual
 *     subscription juggling needed.
 *
 * Performance notes:
 *   - `interactive: false` disables drag/zoom/pitch interactions so
 *     the canvas can't accidentally fight the host map for input.
 *   - `attributionControl: false` + a single setStyle URL keeps the
 *     mini map's tile fetches minimal. The mini map shares the same
 *     style URL as the host (so most tiles are likely warm in the
 *     browser cache).
 *   - We piggyback on the host map's `move` event (per-frame during
 *     animations) and call `jumpTo` (no easing) to keep the mini in
 *     perfect sync without doubling animation cost.
 */
const HostMapPreview: React.FC<{
  hostMap:      mapboxgl.Map;
  hostMapStyle: string | null;
  width:        number;
  height:       number;
}> = ({ hostMap, hostMapStyle, width, height }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const miniRef      = useRef<mapboxgl.Map | null>(null);

  // Initialise the mini map ONCE per hostMap. `hostMap` is the only
  // dep because creating a new mapboxgl.Map is expensive — we don't
  // want to re-create on every parent render. Style + size changes
  // are handled by separate effects below that mutate the existing
  // instance.
  useEffect(() => {
    if (!containerRef.current) return;
    // Snapshot the host's current camera so the mini paints with
    // the correct view on its very first frame (no flash of
    // default-camera while mirroring lags). Defaults are safe
    // fallbacks if the host map hasn't fully initialised yet.
    let center: [number, number] = [0, 0];
    let zoom    = 1;
    let bearing = 0;
    let pitch   = 0;
    try { const c = hostMap.getCenter(); center = [c.lng, c.lat]; } catch { /* ignore */ }
    try { zoom    = hostMap.getZoom();    } catch { /* ignore */ }
    try { bearing = hostMap.getBearing(); } catch { /* ignore */ }
    try { pitch   = hostMap.getPitch();   } catch { /* ignore */ }

    let mini: mapboxgl.Map;
    try {
      mini = new mapboxgl.Map({
        container:          containerRef.current,
        style:              hostMapStyle || 'mapbox://styles/mapbox/streets-v12',
        center,
        zoom,
        bearing,
        pitch,
        interactive:        false,
        attributionControl: false,
        // Disable scroll wheel + box zoom + drag explicitly belt-and-
        // suspenders style; some Mapbox versions only honour the
        // top-level `interactive` flag for some handlers.
        scrollZoom:         false,
        boxZoom:            false,
        dragRotate:         false,
        dragPan:            false,
        keyboard:           false,
        doubleClickZoom:    false,
        touchZoomRotate:    false,
      });
      miniRef.current = mini;
    } catch {
      // Mapbox can fail to construct (no token, no WebGL). The PIP
      // is non-essential — the agent broadcast works fine without
      // it — so we just bail silently and render an empty pane.
      return;
    }

    // Mirror every host camera move into the mini. `move` fires per
    // frame during animations so jumpTo (no internal easing) is
    // exactly what we want — easing here would lag the host.
    const onMove = () => {
      if (!miniRef.current) return;
      try {
        const c = hostMap.getCenter();
        miniRef.current.jumpTo({
          center: [c.lng, c.lat],
          zoom:    hostMap.getZoom(),
          bearing: hostMap.getBearing(),
          pitch:   hostMap.getPitch(),
        });
      } catch { /* host map not ready or torn down — ignore */ }
    };
    hostMap.on('move', onMove);

    return () => {
      try { hostMap.off('move', onMove); } catch { /* ignore */ }
      try { mini.remove(); } catch { /* ignore */ }
      if (miniRef.current === mini) miniRef.current = null;
    };
    // hostMapStyle is intentionally omitted — it's only used as the
    // INITIAL style at construction time. Subsequent style changes
    // are handled by the dedicated style-swap effect below via
    // mini.setStyle() so we don't tear down + re-create the WebGL
    // context every time the host taps a style chip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostMap]);

  // Style swap — when the host changes Mapbox style (e.g. via the
  // teleport HUD's quick-style chips), the mini follows.
  useEffect(() => {
    const mini = miniRef.current;
    if (!mini || !hostMapStyle) return;
    try {
      // Compare against the current style URL when available so we
      // don't reload tiles for no reason on identical strings.
      const cur = (mini.getStyle?.() as any)?.sprite || '';
      if (typeof cur === 'string' && cur && cur === hostMapStyle) return;
      mini.setStyle(hostMapStyle);
    } catch { /* ignore */ }
  }, [hostMapStyle]);

  // Resize the mini when the player toggles between mini / expanded.
  // Without resize() Mapbox keeps the old internal canvas size and
  // renders cropped / stretched after the parent dims change.
  useEffect(() => {
    const mini = miniRef.current;
    if (!mini) return;
    // requestAnimationFrame so the resize lands AFTER the parent's
    // CSS dimensions settle on the new width/height.
    const id = requestAnimationFrame(() => {
      try { mini.resize(); } catch { /* ignore */ }
    });
    return () => cancelAnimationFrame(id);
  }, [width, height]);

  return (
    <div
      ref={containerRef}
      aria-label="Host camera preview"
      title="Mirror of the teleport host's map view"
      style={{
        width,
        height,
        position:                'relative',
        background:              '#0f172a',
        overflow:                'hidden',
        borderLeft:              '1px solid rgba(99, 102, 241, 0.35)',
        // Mirror the right-edge rounding the video pane lost when we
        // showed up — keeps the unified-pill look intact.
        borderBottomRightRadius: 10,
        // Cursor stays default — this is a passive preview, not
        // something the user clicks. The "PIP HOST" badge explains
        // what they're looking at.
        cursor:                  'default',
      }}
    >
      {/* "PIP HOST" affordance label so the viewer knows this isn't
          part of the agent's broadcast. Tiny + unobtrusive. */}
      <span
        style={{
          position:      'absolute',
          top:           4,
          left:          4,
          padding:       '1px 5px',
          background:    'rgba(15, 23, 42, 0.78)',
          color:         '#c7d2fe',
          fontSize:      8.5,
          fontWeight:    800,
          letterSpacing: 0.5,
          borderRadius:  4,
          textTransform: 'uppercase',
          pointerEvents: 'none',
          zIndex:        1,
        }}
      >
        Host view
      </span>
    </div>
  );
};

const containerBaseStyle: React.CSSProperties = {
  display:      'flex',
  flexDirection: 'column',
  borderRadius: 10,
  overflow:     'hidden',
  pointerEvents: 'auto',
  boxShadow:    '0 6px 20px rgba(0, 0, 0, 0.4)',
};

const controlBtnStyle: React.CSSProperties = {
  display:      'inline-flex',
  alignItems:   'center',
  justifyContent: 'center',
  width:        20,
  height:       20,
  background:   'rgba(99, 102, 241, 0.18)',
  color:        '#c7d2fe',
  border:       '1px solid rgba(99, 102, 241, 0.45)',
  borderRadius: 6,
  font:         '700 11px/1 system-ui, -apple-system, sans-serif',
  cursor:       'pointer',
  padding:      0,
};

export default AgentTeleportLivePlayer;
